import { assertIsDefined, isDefined, isNotDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useState } from 'react';
import { FormattedNumber } from 'react-intl';

import { RATES_CONTAINER_HEIGHT } from '@/App/tiles/CashTile/shared/constants.ts';
import { IdleRatesComponent } from '@/App/tiles/CashTile/shared/IdleRatesComponent.tsx';
import { LoadingRatesComponent } from '@/App/tiles/CashTile/shared/LoadingRatesComponent.tsx';
import { RatePanel } from '@/App/tiles/CashTile/shared/RatePanel.tsx';
import { ErrorRates } from '@/App/tiles/shared/ErrorRates.tsx';
import { SaleTier } from '@/App/tiles/shared/SaleTier.tsx';
import { WarningRates } from '@/App/tiles/shared/WarningRates.tsx';
import { getHighlightDigitPosition } from '@/components/ui/FormattedPrice.tsx';
import { checkIfUserCanTrade, checkIfUserInternalSales, useUserContext } from '@/context/UserContext.tsx';
import { useExecuteCashEspMutation, useExecuteCashRfsMutation } from '@/store/cash/api/index.ts';
import { selectCashPriceStatus } from '@/store/cash/selectors/prices.ts';
import type { CashRfsPrice } from '@/store/cash/selectors/prices.types.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { generateExecutionId } from '@/utils/id.ts';
import { deepEqual } from '@/utils/object.ts';

export type SpotRatesProps = {
  tileId: string;
};

/**
 * ⚙️ SpotRates component displays spot FX rates, handles user interactions for bid/ask executions, and manages UI states (idle, loading, streamed).
 *
 * @example
 * <SpotRates tileId="tile-123" />
 *
 * 🚩🚩🚩 @returns JSX.Element - the rendered spot rates UI for the given tile
 */
export const SpotRates = (props: SpotRatesProps) => {
  // 💡 Destructure tileId from props
  const { tileId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();
  const userPreferences = useAppSelector((state) => state.userPreferences);
  const preferedEmails = userPreferences.splitNotifications
    ? userPreferences.splitNotificationsEmailsCash
    : userPreferences.emails;

  // 💡 Getting user type and permissions
  const userContextValue = useUserContext();
  const notificationEmail = preferedEmails.length > 0 ? preferedEmails.join(';') : userContextValue.currentUser.login;

  // 💡 State for tracking the current execution ID for trades
  const [executionId, setExecutionId] = useState(generateExecutionId());

  // 💡 Select and compute all necessary data from the Redux store and other sources for rendering and trading
  const data = useAppSelector((state) => {
    // 💡 Retrieve tile, prices, connection, and user info from the store
    const tile = selectCashTile(state, tileId);
    const priceStatus = selectCashPriceStatus(state, tileId);
    const signalRConnectionId = getSignalRConnectionId(state);

    if (priceStatus.status === 'rfs-not-ready' && priceStatus.isInvalidPrices) {
      return { status: 'invalid-prices' as const };
    }

    if (isNotDefined(tile)) {
      return { status: 'idle' as const };
    }

    if (isDefined(tile.properties.error)) {
      return {
        status: 'idle' as const,
        rateError: tile.properties.error,
      };
    }

    // 💡 Early return if any required data is missing or not ready
    if (
      isNotDefined(tile.properties.currency1) ||
      isNotDefined(tile.properties.currency2) ||
      isNotDefined(priceStatus) ||
      (priceStatus.status !== 'esp-tiering-required' &&
        priceStatus.status !== 'esp-required' &&
        priceStatus.status !== 'esp-pending' &&
        priceStatus.status !== 'esp-price' &&
        priceStatus.status !== 'rfs-pending' &&
        priceStatus.status !== 'rfs-price') ||
      isNotDefined(signalRConnectionId) ||
      isNotDefined(notificationEmail) ||
      isNotDefined(tile.properties.maturityDate)
    ) {
      // 💡 Status is idle if any required data is missing
      return {
        status: 'idle' as const,
      };
    }

    // 💡 Status is loading if prices are loading
    if (
      priceStatus.status === 'esp-tiering-required' ||
      priceStatus.status === 'esp-required' ||
      priceStatus.status === 'esp-pending' ||
      priceStatus.status === 'rfs-pending'
    ) {
      return {
        status: 'loading' as const,
      };
    }

    // 💡 Compute highlight digit position for price formatting
    const highlightDigitPosition = getHighlightDigitPosition(
      priceStatus.currencyPairPrecision,
      priceStatus.commercialMarginSpotPoints,
    );

    // 💡 Determine clientId, fallback to default if not set
    const clientId = selectTileClientId(state, tileId);

    // 💡 Return all relevant data for the streamed state
    return {
      status: 'streamed' as const,

      isTradeCaptureChecking: tile.tradeCapture.status === 'CHECKING',

      currency1: tile.properties.currency1,
      currency2: tile.properties.currency2,
      isNdf: tile.type === 'FxNdf',
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      maturityDate: tile.properties.maturityDate,
      fixingSource: tile.type === 'FxNdf' ? tile.properties.fixingSource : undefined,
      fixingDate: tile.type === 'FxNdf' ? tile.properties.fixingDate : undefined,

      clientId,

      prices: priceStatus,
      signalRConnectionId,
      highlightDigitPosition,

      rateError: tile.properties.error,
    };
  }, deepEqual);

  // 💡 Mutation hook for executing a cash ESP trade, using the current executionId as cache key
  const [executeCashEsp] = useExecuteCashEspMutation({ fixedCacheKey: executionId });

  const [executeCashRfs] = useExecuteCashRfsMutation({
    fixedCacheKey: data.status === 'streamed' && data.prices.status === 'rfs-price' ? data.prices.rfsId : undefined,
  });

  // 💡 Get current user info for permissions and user type
  //const { data: currentUser } = useCurrentUserQuery();

  if (isDefined(data.rateError)) {
    return (
      <ErrorRates code={data.rateError.code} message={data.rateError.message} multipass={data.rateError.multipass} />
    );
  }

  // 💡 Display warning block instead of spot rates if is internal sale and price is invalid and purpose to pass on RFS mode
  if (data.status === 'invalid-prices') {
    return <WarningRates />;
  }

  // 💡 Determine if the user can trade (must be defined, streamed, and not checking trade capture)
  const canTrade =
    data.status === 'streamed' && !data.isTradeCaptureChecking ? checkIfUserCanTrade(userContextValue) : false;

  const rateType = isDefined(data.notional) && data.notional > 0 ? 'executable' : 'indicative'; // Default rate type for bid/ask rates

  // 💡 Determine if the user is an external trader
  const isSaleUser = checkIfUserInternalSales(userContextValue);

  // 🚩 Render (idle or rfs-required state: show placeholders for bid/ask)
  if (data.status === 'idle') {
    return <IdleRatesComponent canTrade={canTrade} />;
  }

  // 🚩 Render (loading state: show loading message)
  if (data.status === 'loading') {
    return <LoadingRatesComponent canTrade={canTrade} />;
  }

  // 💡 Destructure relevant data for the streamed state
  const {
    clientId,
    prices: { rates, spread, currencyPairPrecision },
  } = data;

  const isPriceStale = data.prices.status === 'esp-price' && data.prices.isStreamingUnusable;

  /**
   * ⚡ Handles double-click event to execute a Bid trade if allowed.
   * Listens to: double-click on the left rate area.
   */
  const executeBid = () => {
    if (
      !canTrade ||
      !data.prices.isExecutable ||
      isNotDefined(data.notional) ||
      data.notional <= 0 ||
      isNotDefined(data.notionalCurrencyIndex)
    ) {
      // 💡 User cannot trade  abort execution
      console.error('User cannot trade or prices are not executable');
      return;
    }

    if (isNotDefined(clientId)) {
      // 💡 clientId is missing, abort execution
      console.error('Client ID is not defined');
      return;
    }

    if (data.prices.status === 'rfs-price') {
      executeRfsBid(data.prices);
      return;
    }

    // 💡 Prepare the next executionId for the next trade
    setExecutionId(generateExecutionId());

    // 💡 Execute the cash ESP trade with all required parameters
    sgwtWebAnalytics?.trackEvent('Cash', 'ESP executed', 'bid');

    executeCashEsp({
      tileId,
      executionId,
      quoteId: data.prices.espPrice.quoteId,

      currency1: data.currency1,
      currency2: data.currency2,
      notional: data.notional,
      notionalCurrencyIndex: data.notionalCurrencyIndex,
      signalRConnectionId: data.signalRConnectionId,
      multipassTargetInstance: data.prices.espPrice.multipassInstanceId,
      maturityDate: data.maturityDate,
      clientSpotRate: rates.bid,
      clientId,
      espPrice: data.prices.espPrice,
      way: 'Bid',
      executionClickByFrontTimestamp: Date.now(),
      lastQuoteFromBackTimestamp: data.prices.backTimestamp,
      lastQuoteReceivedByFrontTimestamp: data.prices.frontTimestamp,
      userLocalDateTime: Date.now(),
      notificationEmail,
      ...getNdfExecutionFields(data),
    });
  };

  /**
   * Execute RFS for bid or ask
   */
  const executeRfs = (way: 'Bid' | 'Ask', prices: CashRfsPrice) => {
    if (
      !canTrade ||
      (way === 'Ask' && data.prices.isReadonly) ||
      !data.prices.isExecutable ||
      isNotDefined(clientId) ||
      isNotDefined(data.notional) ||
      data.notional <= 0 ||
      isNotDefined(data.notionalCurrencyIndex)
    ) {
      return;
    }

    const isBid = way === 'Bid';
    const eventLabel = isBid ? 'bid' : 'ask';
    const priceWithoutMargin = isBid ? prices.ratesWithoutMargin.bid : prices.ratesWithoutMargin.ask;
    const priceWithMargin = isBid ? rates.bid : rates.ask;
    const forwardPoints = isBid ? prices.swapPoints.bid : prices.swapPoints.ask;

    sgwtWebAnalytics?.trackEvent('Cash', 'RFS executed', eventLabel);

    executeCashRfs({
      productName: 'FxSpot',
      tileId,
      quoteId: prices.quoteId,
      rfsId: prices.rfsId,
      clientId,
      signalRConnectionId: data.signalRConnectionId,
      currency1: data.currency1,
      currency2: data.currency2,
      notional: data.notional,
      notionalCurrencyIndex: data.notionalCurrencyIndex,
      maturityDate: data.maturityDate,
      way,
      priceWithoutMargin,
      priceWithMargin,
      spotWithoutMargin: prices.execution.spotWithoutMargin,
      spotWithMargin: prices.execution.spotWithMargin,
      forwardPoints,
      executionClickByFrontTimestamp: Date.now(),
      lastQuoteFromBackTimestamp: prices.backTimestamp,
      lastQuoteReceivedByFrontTimestamp: prices.frontTimestamp,
      userLocalDateTime: Date.now(),
      // ...timestamps,
      notificationEmail,
    });
  };

  const executeRfsBid = (prices: CashRfsPrice) => executeRfs('Bid', prices);
  const executeRfsAsk = (prices: CashRfsPrice) => executeRfs('Ask', prices);

  /**
   * ⚡ Handles double-click event to execute an Ask trade if allowed.
   * Listens to: double-click on the right rate area.
   */
  const executeAsk = () => {
    if (
      !canTrade ||
      data.prices.isReadonly ||
      !data.prices.isExecutable ||
      isNotDefined(data.notional) ||
      data.notional <= 0 ||
      isNotDefined(data.notionalCurrencyIndex)
    ) {
      // 💡 User cannot trade  abort execution
      console.error('User cannot trade or prices are not executable');
      return;
    }

    if (isNotDefined(clientId)) {
      // 💡 clientId is missing, abort execution
      console.error('Client ID is not defined');
      return;
    }

    if (data.prices.status === 'rfs-price') {
      executeRfsAsk(data.prices);
      return;
    }

    // 💡 Prepare the next executionId for the next trade
    setExecutionId(generateExecutionId());

    // 💡 Execute the cash ESP trade with all required parameters
    sgwtWebAnalytics?.trackEvent('Cash', 'ESP executed', 'ask');

    executeCashEsp({
      executionId,
      tileId,
      quoteId: data.prices.espPrice.quoteId,

      currency1: data.currency1,
      currency2: data.currency2,
      notional: data.notional,
      notionalCurrencyIndex: data.notionalCurrencyIndex,
      signalRConnectionId: data.signalRConnectionId,
      multipassTargetInstance: data.prices.espPrice.multipassInstanceId,
      maturityDate: data.maturityDate,
      clientSpotRate: rates.ask,
      clientId,
      espPrice: data.prices.espPrice,
      way: 'Ask',
      executionClickByFrontTimestamp: Date.now(),
      lastQuoteFromBackTimestamp: data.prices.backTimestamp,
      lastQuoteReceivedByFrontTimestamp: data.prices.frontTimestamp,
      userLocalDateTime: Date.now(),
      notificationEmail,
      ...getNdfExecutionFields(data),
    });
  };

  // 🚩 Render (streamed state: show bid/ask rates and spread)
  return (
    <HStack fullWidth className="position-relative" style={{ height: RATES_CONTAINER_HEIGHT }} data-e2e="rates">
      <RatePanel
        type="spot"
        side="left"
        way="Bid"
        rate={rates.bid}
        highlightPosition={data.highlightDigitPosition.bid ?? currencyPairPrecision + 1}
        currencyPairPrecision={currencyPairPrecision}
        currency1={data.currency1}
        currency2={data.currency2}
        notionalCurrencyIndex={data.notionalCurrencyIndex ?? 1}
        isSaleUser={isSaleUser}
        isExecutable={data.prices.isExecutable}
        canTrade={canTrade}
        isReadonly={data.prices.isReadonly}
        isPriceStale={isPriceStale}
        rateType={rateType}
        onExecute={executeBid}
      />

      <RatePanel
        type="spot"
        side="right"
        way="Ask"
        rate={rates.ask}
        highlightPosition={data.highlightDigitPosition.ask ?? currencyPairPrecision + 1}
        currencyPairPrecision={currencyPairPrecision}
        currency1={data.currency1}
        currency2={data.currency2}
        notionalCurrencyIndex={data.notionalCurrencyIndex ?? 1}
        isSaleUser={isSaleUser}
        isExecutable={data.prices.isExecutable}
        canTrade={canTrade}
        isReadonly={data.prices.isReadonly}
        isPriceStale={isPriceStale}
        rateType={rateType}
        onExecute={executeAsk}
      />

      {/* 💡 Spread display at the bottom center */}
      <HStack horizontalAlignment="center" className="position-absolute bottom-0 start-0 end-0" data-e2e="spread">
        <div className="px-1 text-black bg-light">
          <FormattedNumber value={spread} minimumFractionDigits={1} maximumFractionDigits={1} />
        </div>
      </HStack>

      {data.prices.isSaleTier && checkIfUserInternalSales(userContextValue) && <SaleTier />}
    </HStack>
  );
};

/**
 * Builds the NDF-specific fields for an ESP execution request.
 * fixingSource/fixingDate are not typed as mandatory when isNdf is true (they come from a Partial<> state shape),
 * but the trading UI never lets the user reach execution for an NDF tile without them being populated.
 */
function getNdfExecutionFields(data: {
  isNdf: boolean;
  fixingSource: string | undefined;
  fixingDate: string | undefined;
}): { isNdf: false } | { isNdf: true; fixingSource: string; fixingDate: string } {
  if (!data.isNdf) {
    return { isNdf: false };
  }

  assertIsDefined(data.fixingSource, 'fixingSource is mandatory to execute an NDF trade');
  assertIsDefined(data.fixingDate, 'fixingDate is mandatory to execute an NDF trade');

  return { isNdf: true, fixingSource: data.fixingSource, fixingDate: data.fixingDate };
}
